import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ReviewService } from "@/api";

/**
 * Default error handler to display error messages using react-toastify.
 * @param {object} err - The error object from the mutation.
 */
const defaultError = (err) => {
  const message = err?.response?.data?.message || err?.message || "An error occurred. Please try again.";
  toast.error(message);
};

/**
 * Custom hook for fetching the review list.
 *
 * @param {Object} params - Query parameters (page, limit, search, freelancer_uuid, reviewer_uuid, rating, start_date, end_date)
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useListReview = (params = {}, onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["review-list", params],
    queryFn: () => {
      return ReviewService.getList(params);
    },
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 30000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Custom hook for fetching a single review's detail.
 *
 * @param {string} uuid - Review UUID
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useGetReview = (uuid, onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["review-detail", uuid],
    queryFn: () => {
      return ReviewService.get(uuid);
    },
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 30000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
    enabled: !!uuid,
  });
};

/**
 * Custom hook for soft-deleting a review.
 *
 * @param {function} onSuccess - Callback to run if the request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useDeleteReview = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid) => {
      return ReviewService.deleteReview(uuid);
    },
    onSuccess: (data, uuid) => {
      queryClient.invalidateQueries({ queryKey: ["review-list"] });
      queryClient.invalidateQueries({ queryKey: ["review-detail", uuid] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

export { useListReview, useGetReview, useDeleteReview };
